/* ============================================================
   When I Grow Up — Landing Page JS (v8)
   ────────────────────────────────────────────────────────────
   Integrated with the storyforge-worker Cloudflare backend.
   All state lives on Cloudflare. Questions match the 20-question
   WIGO canonical questionnaire (three acts).

   FLOW
   ────────────────────
   1. Customer pays on Shopify
   2. Shopify webhook → Worker creates order + signs a 72h access token
   3. Worker fires a Klaviyo event "StoryForge Access Link Ready"
   4. Klaviyo flow emails the customer a link:
         https://www.whenigrowup.shop/?token=<signed-token>
   5. This page picks up the token from the URL, validates via
      GET /api/session, unlocks the questionnaire
   6. Answers POST progressively to /api/answers; photo via
      /api/uploads/request → direct upload → /api/uploads/confirm
   7. On submit, /api/generate/story kicks off
   ============================================================ */

const WORKER_URL = (() => {
  const meta = document.querySelector('meta[name="wigo-worker-url"]');
  const url = meta?.getAttribute('content')?.trim();
  if (!url) {
    console.error('[wigo] worker URL meta tag missing or empty; falling back to production');
    return 'https://api.whenigrowup.shop';
  }
  return url;
})();

// ── Helpers ─────────────────────────────────────────────────────

function getToken()   { return sessionStorage.getItem('wigo_token') || ''; }
function setToken(t)  { sessionStorage.setItem('wigo_token', t); }
function clearToken() { sessionStorage.removeItem('wigo_token'); }

async function api(path, opts = {}) {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');

  const res = await fetch(`${WORKER_URL}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...(opts.headers || {}),
    },
  });

  let body;
  try { body = await res.json(); } catch { body = { ok: false, error: 'Bad JSON' }; }
  if (!body.ok) throw new Error(body.error || `HTTP ${res.status}`);
  return body.data;
}

// ── NAV ─────────────────────────────────────────────────────────

window.addEventListener('scroll', () => {
  const nav = document.getElementById('wigo-nav');
  if (nav) nav.classList.toggle('scrolled', window.scrollY > 30);
});

// ── Reveal-on-scroll ────────────────────────────────────────────

const revealObs = new IntersectionObserver(entries => {
  entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('v'); });
}, { threshold: 0.1 });
document.querySelectorAll('.r').forEach(e => revealObs.observe(e));

// ── Sample modal ────────────────────────────────────────────────

function openModal()  { document.getElementById('wigo-modal').classList.add('open'); }
function closeModal() { document.getElementById('wigo-modal').classList.remove('open'); }
document.getElementById('wigo-modal')?.addEventListener('click', e => {
  if (e.target === document.getElementById('wigo-modal')) closeModal();
});

// ── Token gate ──────────────────────────────────────────────────

function unlockQA() {
  const gate     = document.getElementById('qa-gate');
  const unlocked = document.getElementById('qa-unlocked');
  if (gate)     gate.style.display     = 'none';
  if (unlocked) unlocked.style.display = 'block';
  setTimeout(() => {
    document.getElementById('questionnaire')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, 200);
  bootstrapQA();
}

function showTokenError(msg) {
  const err = document.getElementById('qa-token-error');
  if (!err) return;
  err.textContent   = msg || "That token doesn't match. Check your email and try again.";
  err.style.display = 'block';
  setTimeout(() => err.style.display = 'none', 5000);
}

async function validateToken(inputToken) {
  const token = (inputToken || '').trim();
  if (!token) { showTokenError('Please enter your access token.'); return; }

  const btn = document.querySelector('.qa-gate-return-row button');
  if (btn) { btn.textContent = 'Checking…'; btn.disabled = true; }

  setToken(token);
  try {
    await api('/api/session');
    unlockQA();
  } catch (e) {
    clearToken();
    showTokenError("That token doesn't match any order. Check your email and try again.");
    if (btn) { btn.textContent = 'Unlock →'; btn.disabled = false; }
  }
}

(function bootGate() {
  const params   = new URLSearchParams(window.location.search);
  const urlToken = params.get('token') || '';
  const onQuestionnairePage = !!document.getElementById('qa-unlocked');

  if (!onQuestionnairePage) {
    const tok = urlToken || getToken();
    if (tok) {
      window.location.replace('/pages/questionnaire?token=' + encodeURIComponent(tok));
    }
    return;
  }

  if (urlToken) {
    setToken(urlToken);
    window.history.replaceState({}, '', window.location.pathname);
    api('/api/session')
      .then(() => unlockQA())
      .catch(() => clearToken());
    return;
  }

  if (getToken()) {
    api('/api/session')
      .then(() => unlockQA())
      .catch(() => clearToken());
  }
})();

// ── The 20 canonical WIGO questions ────────────────────────────
// key values match QUESTION_KEYS in worker/src/handlers/answers.ts

const ACTS = [
  { from: 0,  to: 3,  label: "Act 1 — Let's Meet You" },
  { from: 4,  to: 12, label: 'Act 2 — Your Big Dreams' },
  { from: 13, to: 20, label: 'Act 3 — What Really Matters' },
];

const APPEARANCE_SKIN_TONES = [
  { label:'I — very fair, always burns',         value:'I'   },
  { label:'II — fair, burns easily',             value:'II'  },
  { label:'III — light, burns moderately',       value:'III' },
  { label:'IV — olive, tans well',               value:'IV'  },
  { label:'V — brown, rarely burns',             value:'V'   },
  { label:'VI — deeply pigmented, never burns',  value:'VI'  },
];
const APPEARANCE_EYE_COLOURS = [
  { label:'Blue',        value:'blue'        },
  { label:'Light green', value:'light_green' },
  { label:'Dark green',  value:'dark_green'  },
  { label:'Light brown', value:'light_brown' },
  { label:'Dark brown',  value:'dark_brown'  },
  { label:'Grey',        value:'grey'        },
];
const APPEARANCE_HAIR_COLOURS = [
  { label:'Black',       value:'black'       },
  { label:'Dark brown',  value:'dark_brown'  },
  { label:'Brown',       value:'brown'       },
  { label:'Light brown', value:'light_brown' },
  { label:'Blonde',      value:'blonde'      },
  { label:'Red',         value:'red'         },
  { label:'Grey',        value:'grey'        },
  { label:'White',       value:'white'       },
];
const APPEARANCE_HAIR_TEXTURES = [
  { label:'Straight', value:'straight' },
  { label:'Wavy',     value:'wavy'     },
  { label:'Curly',    value:'curly'    },
  { label:'Coily',    value:'coily'    },
];
const APPEARANCE_HAIR_LENGTHS = [
  { label:'Short (above ears)',     value:'short'    },
  { label:'Shoulder length',        value:'shoulder' },
  { label:'Long (below shoulders)', value:'long'     },
];

const Qs = [
  // ── Act 1 ──────────────────────────────────────────────────────
  { key:'name',
    q:"What's your name?",
    hint:"Just your first name is perfect — what people who love you call you.",
    type:'text', ph:'e.g. Sarah' },
  { key:'age',
    q:"How old are you?",
    hint:"Numbers, halves, quarters — however you'd say it.",
    type:'text', ph:'e.g. 5 and a half' },
  { key:'gender',
    q:"Are you a boy or a girl?",
    hint:"So we can draw you just right.",
    type:'choices',
    choices:['Boy','Girl'] },

  { key:'personality_word',
    q:"If your best friend had to describe you in one word, what would it be?",
    hint:"Just one — the one that's most you.",
    type:'ta', ph:'Kind' },

  // ── Act 2 ──────────────────────────────────────────────────────
  { key:'dream_job',
    q:"What job do you dream of having when you grow up? And why does that feel so exciting?",
    hint:'Any job is welcome — real or imaginary! 😄',
    type:'ta', ph:'An astronaut who also bakes, or something even better...' },

  { key:'dream_home',
  q:'Which of these would be your dream home when you grow up?',
  hint:"The one that makes you smile most 🏡 — the more descriptive you can be, the better. Your imagination is the limit!",
  type:'choices',
  choices:['🏖 Beach House','🚤 Boathouse','🏰 Castle','🚜 Farm House','🌊 Floating House','🧊 Igloo','🌴 Jungle House','🗼 Lighthouse','👑 Palace','🌳 Treehouse','⛏ Underground House'],
  allowOther:true,
  otherLabel:'✨ Something else (describe it!)',
  otherPlaceholder:'A house where the jungle meets the sea, a cabin in the mountains with a waterfall...',
},

{ key:'dream_pet',
    q:"If you could have absolutely any animal in the world as a pet — real or made up — what would you choose? And what would you name it?",
    hint:'Dragons very welcome 🐉',
    type:'ta', ph:'A baby dragon called Biscuit...' },

  { key:'favourite_food',
    q:"What's your all-time favourite food — the one that makes you do a happy dance when you find out it's for dinner? What makes it so delicious?",
    hint:"The one that makes your whole face light up 🎆",
    type:'ta', ph:"Spaghetti, because it's slurpy and it gets everywhere..." },

  { key:'favourite_colour',
    q:'What is your favourite colour?',
    hint:'The one you reach for first every time 🎨',
    type:'choices',
    choices:['💗 Pink','💙 Blue','❤️ Red','💜 Purple','💚 Green','💛 Yellow','🧡 Orange','🖤 Black','🥇 Gold','🩵 Turquoise','🤍 White','🩶 Silver'] },

  { key:'perfect_saturday',
    q:"When you're all grown up, what's one thing you'd love to do on a Saturday just for fun?",
    hint:'Your perfect grown-up weekend day ✨',
    type:'ta', ph:'Wake up late, go to a bookshop, eat ice cream for lunch...' },

  { key:'something_to_build',
    q:'If you could create or build something that would make the world better, what would it be?',
    hint:'Pick one, or tell us your own idea 💡',
    type:'choices',
    choices:['🏫 A school','🏥 A hospital','🛝 A playground','🐾 A shelter for animals','🚀 A rocket ship'],
    allowOther:true,
    otherLabel:'✨ Something else (tell us!)',
    otherPlaceholder:'A bird hospital, an underwater school, a kindness machine...' },

  { key:'superpower',
    q:"If you could have any superpower in the world, what would it be — and why?",
    hint:"It can be a real superpower from a film, or one you make up. Then tell us why you'd want it.",
    type:'ta', ph:"I'd want to fly so I could visit Grandma whenever I wanted..." },

  { key:'dream_destination',
    q:"If you could teleport anywhere in the whole world — or even to a made-up magical place — where would you go? And what's the first thing you'd do when you got there?",
    hint:'Real or imaginary — both are brilliant 🌍',
    type:'ta', ph:"To a floating island in the clouds, and I'd..." },

  // ── Act 3 ──────────────────────────────────────────────────────
  { key:'never_stop_doing',
    q:"What's the one thing you hope you never, ever stop doing, no matter how old you get? The thing that always makes you feel like you?",
    hint:"The thing that's always been yours 💛",
    type:'ta', ph:'Drawing, or singing in the car, or...' },

  { key:'skill_to_master',
    q:"If you could become absolutely amazing at one thing — something you'd practise every day until you were the best — what would it be?",
    hint:"A skill you'd give anything to master ⚡",
    type:'ta', ph:"Playing piano, making the world's best pancakes..." },

  { key:'always_in_fridge',
    q:'When you have your very own home, what one thing will always be waiting for you in the fridge — the thing that would make you really happy to come home to every day?',
    hint:'Your grown-up fridge staple 🥛',
    type:'text', ph:'Chocolate milk, cheese, grapes...' },

  { key:'virtue',
    q:"When you're a grown-up, what word would you like people to use to describe you?",
    hint:'The word that means most to you ✨',
    type:'choices',
    choices:[
      "💛 Kind — you always look after people",
      "🦁 Brave — you try things even when they're scary",
      '😂 Funny — you make everyone laugh',
      '🔥 Determined — you never give up',
      '🔍 Curious — you always want to know how things work',
      '🎨 Creative — you see the world differently',
    ] },

  { key:'proud_person_appearance',
    q:'Tell us about the person you most want to make proud',
    hint:"Their name and a few quick details so we can draw them in as a surprise! 🎨",
    type:'appearance_grid',
    photoRole:'proud_person_photo',
    relationshipField:{
  key:'make_proud_person',  // was: make_proud_description
  label:'Who do you most want to make proud?',
      placeholder:'My mum, my grandad, my little brother...',
      required:true,
    },
    subfields:[
      { key:'proud_person_skin_tone',    label:'Skin tone',    choices:APPEARANCE_SKIN_TONES   },
      { key:'proud_person_eye_colour',   label:'Eye colour',   choices:APPEARANCE_EYE_COLOURS  },
      { key:'proud_person_hair_colour',  label:'Hair colour',  choices:APPEARANCE_HAIR_COLOURS },
      { key:'proud_person_hair_texture', label:'Hair texture', choices:APPEARANCE_HAIR_TEXTURES },
      { key:'proud_person_hair_length',  label:'Hair length',  choices:APPEARANCE_HAIR_LENGTHS },
    ],
    notesField:{
      key:'proud_person_appearance_notes',
      label:'Anything else worth knowing? (optional, 200 chars)',
      maxLength:200,
    } },

  { key:'child_appearance',
    q:'What does your child look like?',
    hint:'Quick details so the illustrator can lock the right look.',
    type:'appearance_grid',
    photoRole:'child_photo',
    subfields:[
      { key:'skin_tone',    label:'Skin tone',    choices:APPEARANCE_SKIN_TONES   },
      { key:'eye_colour',   label:'Eye colour',   choices:APPEARANCE_EYE_COLOURS  },
      { key:'hair_colour',  label:'Hair colour',  choices:APPEARANCE_HAIR_COLOURS },
      { key:'hair_texture', label:'Hair texture', choices:APPEARANCE_HAIR_TEXTURES },
      { key:'hair_length',  label:'Hair length',  choices:APPEARANCE_HAIR_LENGTHS },
    ],
    notesField:{
      key:'appearance_notes',
      label:'Anything else worth knowing? (optional, 200 chars)',
      maxLength:200,
    } },

  { key:'best_thing_about_age',
    q:"What's the best thing about being exactly your age right now? What can you do, feel, or dream about that makes this time in your life really special?",
    hint:"The magic of being you, right now ✨",
    type:'ta', ph:'I can ride my bike on my own, and I know loads about whales...' },

  { key:'message_to_world',
    q:'If you could tell the whole world just one thing, what would it be?',
    hint:"A short note — up to 100 characters.",
    type:'ta', ph:'Be kind. To everyone.',
    maxLength: 100 },
];

const TOTAL_QS = Qs.length;

let idx = 0;
const A = {};
let inReview = false;
let cameFromReview = false;

const pickedOther = new Set();
const photos = {
  child_photo:        { confirmed: false, skipped: false, src: null, uploading: false },
  proud_person_photo: { confirmed: false, skipped: false, src: null, uploading: false },
};

const firstName = () => {
  const v = A.name || '';
  const cleaned = v.replace(/^\s*(i'?m|i am|my name is|i'?m called)\s+/i, '').trim();
  const first = cleaned.split(/[\s,]+/)[0] || '';
  return first || 'you';
};

function knownAnswerKeys() {
  const keys = new Set();
  Qs.forEach(q => {
    if (q.type === 'appearance_grid') {
      if (q.relationshipField) keys.add(q.relationshipField.key);
      q.subfields.forEach(sf => keys.add(sf.key));
      if (q.notesField) keys.add(q.notesField.key);
    } else if (q.key) {
      keys.add(q.key);
    }
  });
  return keys;
}

function gridIsAnswered(q) {
  if (q.relationshipField?.required) {
    const v = A[q.relationshipField.key];
    if (v == null || String(v).trim() === '') return false;
  }
  return true;
}

function isOtherSelected(q) {
  if (!q.allowOther) return false;
  if (pickedOther.has(q.key)) return true;
  const v = A[q.key];
  if (v == null || v === '') return false;
  return !q.choices.some(c => (typeof c === 'object' ? c.value : c) === v);
}

function isQAnswered(q) {
  if (q.optional) return true;
  if (q.type === 'appearance_grid') return gridIsAnswered(q);
  return A[q.key] != null && String(A[q.key]).trim() !== '';
}

function getMissingAnswers() {
  const missing = [];
  Qs.forEach((q, i) => {
    if (q.optional) return;
    if (q.type === 'appearance_grid') {
      if (q.relationshipField?.required) {
        const v = A[q.relationshipField.key];
        if (v == null || String(v).trim() === '') {
          missing.push({ idx: i, label: q.relationshipField.label });
        }
      }
      return;
    }
    if (A[q.key] == null || String(A[q.key]).trim() === '') {
      missing.push({ idx: i, label: reviewLabel(q) });
    }
  });
  return missing;
}

async function bootstrapQA() {
  try {
    const session = await api('/api/session');
    const known = knownAnswerKeys();
    const answers = session.questionnaire?.answers || {};
    for (const [k, v] of Object.entries(answers)) {
      if (known.has(k)) A[k] = v;
    }
    (session.uploads || []).forEach(u => {
      const slot = photos[u.role];
      if (slot) {
        slot.confirmed = true;
        slot.src = u.deliveryUrl;
      }
    });
    const firstUnanswered = Qs.findIndex(q => !isQAnswered(q));
    if (firstUnanswered === -1) {
      idx = TOTAL_QS;
      inReview = true;
      renderReview();
      return;
    }
    if (firstUnanswered > 0) idx = firstUnanswered;
  } catch (e) {
    console.warn('Bootstrap failed:', e.message);
  }
  renderQ();
}

function getActLabel(i) {
  const a = ACTS.find(act => i >= act.from && i <= act.to);
  return a ? a.label : '';
}

function updateTrail() {
  const trail = document.getElementById('qa-trail');
  if (!trail) return;
  trail.innerHTML = '';
  const answered = Qs.slice(0, idx).filter(q => {
    if (q.type === 'appearance_grid') return gridIsAnswered(q);
    return A[q.key] != null && A[q.key] !== '';
  });
  answered.slice(-3).forEach(q => {
    const qi  = Qs.indexOf(q);
    const val = q.type === 'appearance_grid'
      ? '🎨 Look details added'
      : (A[q.key] || '');
    if (!val) return;
    const row = document.createElement('div');
    row.className = 'qa-trail-item';
    row.innerHTML = `<span class="qa-trail-q">Q${qi+1}</span><span class="qa-trail-a">${val.toString().slice(0,80)}${val.length>80?'…':''}</span>`;
    trail.appendChild(row);
  });
}

function renderQ() {
  inReview = false;
  const trail = document.getElementById('qa-trail');
  if (trail) trail.style.display = '';

  const q = Qs[idx];
  const pct = Math.round((idx / TOTAL_QS) * 100);

  document.getElementById('qa-prog-fill').style.width  = Math.max(5, pct) + '%';
  document.getElementById('qa-prog-label').textContent = `Question ${idx + 1} of ${TOTAL_QS}`;
  document.getElementById('qa-prog-pct').textContent   = pct + '%';
  document.getElementById('qa-q-label').textContent    = `Question ${idx + 1} of ${TOTAL_QS}`;
  document.getElementById('qa-back').disabled          = idx === 0;
  setQaNextError(null);

  const chTag = document.getElementById('qa-chapter-tag');
  if (chTag) chTag.textContent = getActLabel(idx);

  const nextLabel = document.getElementById('qa-next-label');
  if (nextLabel) {
    if (cameFromReview) nextLabel.textContent = 'Save & return to review';
    else if (idx === TOTAL_QS - 1) nextLabel.textContent = 'Review answers';
    else nextLabel.textContent = 'Continue';
  }

  const nm = firstName();
  const proud = (A.make_proud_person || '').trim() || 'the person you want to make proud';
  const tpl = s => (s || '').replace(/\{name\}/g, nm).replace(/\{make_proud_person\}/g, proud);
  document.getElementById('qa-question').textContent = tpl(q.q);
  document.getElementById('qa-hint').textContent     = tpl(q.hint);

  const area = document.getElementById('qa-input-area');
  area.innerHTML = '';

  if (q.type === 'text') {
    const i = document.createElement('input');
    i.type = 'text'; i.className = 'qa-input';
    i.placeholder = (q.ph || '').replace(/\{name\}/g, nm);
    i.value = A[q.key] || '';
    i.addEventListener('keydown', e => { if (e.key === 'Enter') qaNext(); });
    area.appendChild(i);
    setTimeout(() => i.focus(), 50);

  } else if (q.type === 'ta') {
    const t = document.createElement('textarea');
    t.className = 'qa-ta';
    t.placeholder = (q.ph || '').replace(/\{name\}/g, nm);
    t.value = A[q.key] || '';
    if (q.maxLength) t.maxLength = q.maxLength;
    area.appendChild(t);

    if (q.maxLength) {
      const counter = document.createElement('div');
      counter.className = 'qa-char-counter';
      counter.style.cssText = 'text-align:right;font-size:.75rem;color:var(--ink-mist);margin-top:.35rem;';
      const updateCounter = () => {
        const n = t.value.length;
        counter.textContent = `${n} / ${q.maxLength}`;
        counter.style.color = n > q.maxLength * 0.9 ? '#C45E38' : 'var(--ink-mist)';
      };
      t.addEventListener('input', updateCounter);
      updateCounter();
      area.appendChild(counter);
    }

    setTimeout(() => t.focus(), 50);

  } else if (q.type === 'choices') {
    const renderChoices = () => {
      area.innerHTML = '';
      const isOther = isOtherSelected(q);

      const w = document.createElement('div'); w.className = 'qa-choices';
      q.choices.forEach(c => {
        const isObj  = c !== null && typeof c === 'object';
        const label  = isObj ? c.label : c;
        const stored = isObj ? c.value : c;
        const b = document.createElement('div');
        b.className = 'qa-choice' + (!isOther && A[q.key] === stored ? ' sel' : '');
        b.textContent = label;
        b.addEventListener('click', () => {
          pickedOther.delete(q.key);
          A[q.key] = stored;
          setQaNextError(null);
          renderChoices();
        });
        w.appendChild(b);
      });

      if (q.allowOther) {
        const b = document.createElement('div');
        b.className = 'qa-choice' + (isOther ? ' sel' : '');
        b.textContent = q.otherLabel || '✨ Something else (tell us!)';
        b.addEventListener('click', () => {
          if (!isOther) { pickedOther.add(q.key); A[q.key] = ''; }
          setQaNextError(null);
          renderChoices();
          area.querySelector('.qa-other-input')?.focus();
        });
        w.appendChild(b);
      }

      area.appendChild(w);

      if (isOther) {
        const t = document.createElement('input');
        t.type = 'text';
        t.className = 'qa-input qa-other-input';
        t.placeholder = q.otherPlaceholder || 'Tell us...';
        t.value = A[q.key] || '';
        t.style.cssText = 'margin-top:.85rem';
        t.addEventListener('input', () => {
          A[q.key] = t.value;
          setQaNextError(null);
        });
        t.addEventListener('keydown', e => { if (e.key === 'Enter') qaNext(); });
        area.appendChild(t);
      }
    };
    renderChoices();

  } else if (q.type === 'appearance_grid') {
    renderAppearanceGrid(area, q, nm);
  }

  updateTrail();

  const card = document.getElementById('qa-card');
  card.classList.remove('exit');
  card.classList.add('enter');
  requestAnimationFrame(() => requestAnimationFrame(() => card.classList.remove('enter')));
}

async function compressImage(file, maxDim = 1920, quality = 0.82) {
  return new Promise(resolve => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width <= maxDim && height <= maxDim) { resolve(file); return; }
      const scale = maxDim / Math.max(width, height);
      width = Math.round(width * scale); height = Math.round(height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob(blob => resolve(blob || file), 'image/jpeg', quality);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}

function ensureFileInput() {
  let fi = document.getElementById('wigo-file-input');
  if (!fi) {
    fi = document.createElement('input');
    fi.type = 'file'; fi.id = 'wigo-file-input';
    fi.accept = 'image/jpeg,image/png,image/webp';
    fi.style.display = 'none';
    document.body.appendChild(fi);
  }
  return fi;
}

function openFilePicker(role) {
  const fi = ensureFileInput();
  fi.dataset.role = role;
  fi.click();
}

function renderPhotoStep(area, nm, role) {
  const slot = photos[role];
  if (!slot) return;

  if (slot.uploading) {
    if (!document.getElementById('wigo-spin-style')) {
      const s = document.createElement('style');
      s.id = 'wigo-spin-style';
      s.textContent = '@keyframes wigo-spin{to{transform:rotate(360deg)}}';
      document.head.appendChild(s);
    }
    const loading = document.createElement('div');
    loading.style.cssText = 'text-align:center;padding:1.5rem 0';
    loading.innerHTML = '<div style="display:inline-block;width:36px;height:36px;border:3px solid #e6e0d8;border-top-color:var(--ink);border-radius:50%;animation:wigo-spin .75s linear infinite"></div><p style="margin:.75rem 0 0;font-size:.9rem;color:var(--ink-mist)">Uploading photo…</p>';
    area.appendChild(loading);
    return;
  }

  if (slot.confirmed && slot.src) {
    const pd = document.createElement('div'); pd.className = 'qa-upload-prev';
    pd.innerHTML = `
      <img src="${slot.src}" alt="Photo">
      <p class="qa-upload-status">✅ Uploaded</p>
      <button class="qa-upload-chg" type="button" data-role="${role}">
        Change photo
      </button>`;
    pd.querySelector('.qa-upload-chg').addEventListener('click', () => openFilePicker(role));
    area.appendChild(pd);
  } else if (slot.skipped) {
    const skipped = document.createElement('div');
    skipped.className = 'qa-upload-prev';
    skipped.innerHTML = `
      <p class="qa-upload-status">📝 No photo — that's fine</p>
      <button class="qa-upload-chg" type="button" data-role="${role}">
        Upload a photo instead
      </button>`;
    skipped.querySelector('.qa-upload-chg').addEventListener('click', () => {
      slot.skipped = false;
      renderQ();
    });
    area.appendChild(skipped);
  } else {
    const lbl = document.createElement('div');
    lbl.className = 'qa-upload-lbl';
    lbl.style.cursor = 'pointer';
    lbl.innerHTML = `
      <div class="big">📸</div>
      <strong>Tap to upload a photo${role === 'child_photo' ? ` of ${nm}` : ''}</strong>
      <span>Optional · JPG or PNG · helps the illustrator ✨</span>`;
    lbl.addEventListener('click', () => openFilePicker(role));
    area.appendChild(lbl);

    const skipBtn = document.createElement('button');
    skipBtn.type = 'button';
    skipBtn.className = 'qa-upload-skip';
    skipBtn.textContent = 'Skip the photo';
    skipBtn.style.cssText =
      'display:block;margin:.85rem auto 0;background:none;border:none;' +
      'color:var(--ink-mist);font-size:.85rem;text-decoration:underline;' +
      'cursor:pointer;font-family:inherit';
    skipBtn.onclick = () => { slot.skipped = true; renderQ(); };
    area.appendChild(skipBtn);
  }

  const fi = ensureFileInput();
  fi.onchange = async function () {
    const file = this.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      alert('Photo is too large — please use an image under 10MB.');
      return;
    }
    const activeRole = this.dataset.role || role;
    const activeSlot = photos[activeRole];
    if (!activeSlot) return;

    activeSlot.uploading = true;
    activeSlot.src = null;
    renderQ();

    try {
      const compressed = await compressImage(file);
      const uploadFile = new File([compressed], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' });
      const previewUrl = URL.createObjectURL(compressed);

      const { uploadId, uploadUrl, cfImageId } = await api('/api/uploads/request', {
        method: 'POST',
        body: JSON.stringify({ role: activeRole, filename: uploadFile.name }),
      });
      const form = new FormData();
      form.append('file', uploadFile);
      const uploadRes = await fetch(uploadUrl, { method: 'POST', body: form });
      if (!uploadRes.ok) throw new Error(`CF upload ${uploadRes.status}`);

      await api('/api/uploads/confirm', {
        method: 'POST',
        body: JSON.stringify({ uploadId, cfImageId }),
      });

      activeSlot.confirmed = true;
      activeSlot.skipped = false;
      activeSlot.uploading = false;
      activeSlot.src = previewUrl;
      this.value = '';
      renderQ();
    } catch (e) {
      console.error('Photo upload failed:', e);
      activeSlot.uploading = false;
      activeSlot.src = null;
      renderQ();
      alert(`Photo upload failed: ${e.message}. Please try again.`);
    }
  };
}

function renderAppearanceGrid(area, q, nm) {
  if (q.relationshipField) {
    const rf = q.relationshipField;
    const row = document.createElement('div');
    row.className = 'qa-grid-row';
    row.style.cssText = 'margin-bottom:1.25rem';
    const label = document.createElement('div');
    label.className = 'qa-grid-label';
    label.style.cssText = 'font-size:.85rem;font-weight:600;margin-bottom:.45rem;color:var(--ink)';
    label.textContent = rf.label;
    row.appendChild(label);
    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'qa-input';
    input.dataset.relationshipKey = rf.key;
    input.placeholder = rf.placeholder || '';
    input.value = A[rf.key] || '';
    input.addEventListener('input', () => { A[rf.key] = input.value; });
    row.appendChild(input);
    area.appendChild(row);
  }

  if (q.photoRole) {
    const photoArea = document.createElement('div');
    photoArea.className = 'qa-grid-photo';
    photoArea.style.cssText = 'margin-bottom:.75rem';
    renderPhotoStep(photoArea, nm, q.photoRole);
    area.appendChild(photoArea);
  }

  const helperMsg = document.createElement('p');
  helperMsg.style.cssText = 'font-size:.78rem;color:var(--ink-mist);margin:.1rem 0 1.25rem;line-height:1.5';
  helperMsg.textContent = "Descriptions are optional if you've uploaded a photo — though picking an eye colour is helpful, as it's often hard to tell from images.";
  area.appendChild(helperMsg);

  q.subfields.forEach(sf => {
    const row = document.createElement('div');
    row.className = 'qa-grid-row';
    row.style.cssText = 'margin-bottom:1.25rem';
    const label = document.createElement('div');
    label.className = 'qa-grid-label';
    label.style.cssText = 'font-size:.85rem;font-weight:600;margin-bottom:.45rem;color:var(--ink)';
    label.textContent = sf.label;
    row.appendChild(label);
    const chips = document.createElement('div');
    chips.className = 'qa-choices';
    sf.choices.forEach(c => {
      const isObj  = c !== null && typeof c === 'object';
      const lbl    = isObj ? c.label : c;
      const stored = isObj ? c.value : c;
      const b = document.createElement('div');
      b.className = 'qa-choice' + (A[sf.key] === stored ? ' sel' : '');
      b.textContent = lbl;
      b.addEventListener('click', () => {
        chips.querySelectorAll('.qa-choice').forEach(x => x.classList.remove('sel'));
        b.classList.add('sel');
        A[sf.key] = stored;
        setQaNextError(null);
      });
      chips.appendChild(b);
    });
    row.appendChild(chips);
    area.appendChild(row);
  });

  if (q.notesField) {
    const nf = q.notesField;
    const row = document.createElement('div');
    row.className = 'qa-grid-row';
    row.style.cssText = 'margin-bottom:1.25rem';
    const label = document.createElement('div');
    label.className = 'qa-grid-label';
    label.style.cssText = 'font-size:.85rem;font-weight:600;margin-bottom:.45rem;color:var(--ink)';
    label.textContent = nf.label;
    row.appendChild(label);
    const t = document.createElement('textarea');
    t.className = 'qa-ta';
    t.dataset.notesKey = nf.key;
    t.value = A[nf.key] || '';
    if (nf.maxLength) t.maxLength = nf.maxLength;
    row.appendChild(t);
    if (nf.maxLength) {
      const counter = document.createElement('div');
      counter.className = 'qa-char-counter';
      counter.style.cssText = 'text-align:right;font-size:.75rem;color:var(--ink-mist);margin-top:.35rem;';
      const update = () => {
        const n = t.value.length;
        counter.textContent = `${n} / ${nf.maxLength}`;
        counter.style.color = n > nf.maxLength * 0.9 ? '#C45E38' : 'var(--ink-mist)';
      };
      t.addEventListener('input', update);
      update();
      row.appendChild(counter);
    }
    area.appendChild(row);
  }
}

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, c => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));
}

function reviewLabel(q) {
  const txt = (q.q || '').replace(/\?$/, '').trim();
  return txt.length > 70 ? txt.slice(0, 67) + '…' : txt;
}

function reviewSummary(q) {
  if (q.type === 'appearance_grid') {
    const parts = [];
    if (q.relationshipField && A[q.relationshipField.key]) {
      parts.push(`<strong>${escapeHtml(A[q.relationshipField.key])}</strong>`);
    }
    const enums = q.subfields.map(sf => {
      const v = A[sf.key];
      if (!v) return null;
      const choice = sf.choices.find(c => (typeof c === 'object' ? c.value : c) === v);
      const lbl = choice ? (typeof choice === 'object' ? choice.label : choice) : v;
      return escapeHtml(String(lbl).split(' — ')[0]);
    }).filter(Boolean);
    if (enums.length) parts.push(enums.join(' · '));
    if (q.notesField && A[q.notesField.key]) {
      parts.push(`<em style="color:var(--ink-mist)">"${escapeHtml(A[q.notesField.key])}"</em>`);
    }
    let summary = parts.join('<br>');
    if (q.photoRole) {
      const p = photos[q.photoRole];
      if (p?.confirmed && p.src) {
        summary = `<img src="${escapeHtml(p.src)}" alt="" style="float:right;width:48px;height:48px;object-fit:cover;border-radius:.4rem;margin:0 0 .25rem .5rem">` + summary;
      } else if (p?.skipped) {
        summary += '<br><span style="font-size:.75rem;color:var(--ink-mist)">📝 No photo</span>';
      }
    }
    return summary || null;
  }
  const v = A[q.key];
  if (v == null || v === '') return null;
  return escapeHtml(v);
}

function renderReview() {
  inReview = true;
  cameFromReview = false;
  setQaNextError(null);

  document.getElementById('qa-prog-fill').style.width  = '100%';
  document.getElementById('qa-prog-pct').textContent   = '100%';
  document.getElementById('qa-prog-label').textContent = 'Final review';
  document.getElementById('qa-q-label').textContent    = 'Final review';
  document.getElementById('qa-back').disabled          = false;

  const chTag = document.getElementById('qa-chapter-tag');
  if (chTag) chTag.textContent = 'Almost there';

  document.getElementById('qa-question').textContent = 'Ready to submit your book?';
  document.getElementById('qa-hint').textContent     = 'Take a moment to check your answers — tap Edit to change anything.';

  const missing = getMissingAnswers();
  const nextLabel = document.getElementById('qa-next-label');
  if (nextLabel) nextLabel.textContent = missing.length > 0 ? 'Fix missing answers' : 'Submit my book';

  const trail = document.getElementById('qa-trail');
  if (trail) trail.style.display = 'none';

  const area = document.getElementById('qa-input-area');
  area.innerHTML = '';

  if (missing.length > 0) {
    const warn = document.createElement('div');
    warn.className = 'qa-review-warning';
    warn.setAttribute('role', 'alert');
    warn.setAttribute('aria-live', 'assertive');
    warn.style.cssText =
      'background:#FBE9E3;border:1px solid #C45E38;border-radius:.6rem;' +
      'padding:1rem 1.1rem;margin-bottom:1.25rem;color:#7a3818';
    const head = document.createElement('div');
    head.style.cssText = 'font-weight:600;margin-bottom:.5rem';
    head.textContent = `You have ${missing.length} question${missing.length === 1 ? '' : 's'} left to answer:`;
    warn.appendChild(head);

    const ul = document.createElement('ul');
    ul.style.cssText = 'margin:0;padding-left:1.1rem';
    missing.forEach(m => {
      const li = document.createElement('li');
      li.style.cssText = 'margin:.2rem 0';
      const link = document.createElement('a');
      link.href = '#';
      link.textContent = m.label;
      link.style.cssText = 'color:#7a3818;text-decoration:underline;cursor:pointer';
      link.addEventListener('click', e => {
        e.preventDefault();
        cameFromReview = true;
        inReview = false;
        idx = m.idx;
        const card = document.getElementById('qa-card');
        card.classList.add('exit');
        setTimeout(() => { card.classList.remove('exit'); renderQ(); }, 200);
      });
      li.appendChild(link);
      ul.appendChild(li);
    });
    warn.appendChild(ul);
    area.appendChild(warn);
  }

  const list = document.createElement('div');
  list.className = 'qa-review-list';
  list.style.cssText =
    'display:flex;flex-direction:column;max-height:55vh;overflow-y:auto;' +
    'border-top:1px solid #e6e0d8;border-bottom:1px solid #e6e0d8;margin:.25rem 0';

  Qs.forEach((q, i) => {
    const summary = reviewSummary(q);
    if (!summary) return;

    const item = document.createElement('div');
    item.className = 'qa-review-item';
    item.style.cssText =
      'display:flex;gap:.75rem;align-items:flex-start;justify-content:space-between;' +
      'padding:.75rem .25rem;border-bottom:1px solid #f1ece5';

    const left = document.createElement('div');
    left.style.cssText = 'flex:1;min-width:0';

    const lbl = document.createElement('div');
    lbl.style.cssText =
      'font-size:.7rem;text-transform:uppercase;letter-spacing:.05em;' +
      'color:var(--ink-mist);margin-bottom:.25rem;font-weight:600';
    lbl.textContent = reviewLabel(q);
    left.appendChild(lbl);

    const val = document.createElement('div');
    val.style.cssText =
      'font-size:.92rem;color:var(--ink);line-height:1.45;' +
      'word-wrap:break-word;overflow-wrap:break-word';
    val.innerHTML = summary;
    left.appendChild(val);
    item.appendChild(left);

    const editBtn = document.createElement('button');
    editBtn.type = 'button';
    editBtn.className = 'qa-review-edit';
    editBtn.textContent = '✎ Edit';
    editBtn.style.cssText =
      'background:none;border:none;color:#3A6F3A;font-size:.85rem;cursor:pointer;' +
      'flex-shrink:0;font-family:inherit;text-decoration:underline;padding:.2rem .4rem';
    editBtn.addEventListener('click', () => {
      cameFromReview = true;
      inReview = false;
      idx = i;
      const card = document.getElementById('qa-card');
      card.classList.add('exit');
      setTimeout(() => { card.classList.remove('exit'); renderQ(); }, 200);
    });
    item.appendChild(editBtn);

    list.appendChild(item);
  });

  area.appendChild(list);

  const keep = document.createElement('button');
  keep.type = 'button';
  keep.className = 'qa-review-keep';
  keep.textContent = '← Keep editing from the start';
  keep.style.cssText =
    'display:block;margin:1rem auto 0;background:none;border:none;' +
    'color:var(--ink-mist);font-size:.85rem;text-decoration:underline;' +
    'cursor:pointer;font-family:inherit';
  keep.addEventListener('click', () => {
    inReview = false;
    cameFromReview = false;
    idx = 0;
    const card = document.getElementById('qa-card');
    card.classList.add('exit');
    setTimeout(() => { card.classList.remove('exit'); renderQ(); }, 200);
  });
  area.appendChild(keep);

  const card = document.getElementById('qa-card');
  card.classList.add('enter');
  requestAnimationFrame(() => requestAnimationFrame(() => card.classList.remove('enter')));
}

async function qaNext() {
  if (inReview) {
    const missing = getMissingAnswers();
    if (missing.length > 0) {
      setQaNextError(`${missing.length} question${missing.length === 1 ? '' : 's'} still need answering — see the list above.`);
      document.querySelector('.qa-review-warning')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    return finishQuestionnaire();
  }

  const q = Qs[idx];
  if (q.type === 'text') { const i = document.querySelector('#qa-input-area .qa-input'); if (i) A[q.key] = i.value; }
  else if (q.type === 'ta') { const t = document.querySelector('#qa-input-area .qa-ta'); if (t) A[q.key] = t.value; }
  else if (q.type === 'appearance_grid') {
    const rfInput = document.querySelector('#qa-input-area input[data-relationship-key]');
    if (rfInput && q.relationshipField) A[q.relationshipField.key] = rfInput.value;
    const t = document.querySelector('#qa-input-area textarea[data-notes-key]');
    if (t && q.notesField) A[q.notesField.key] = t.value;
  }

  const btn = document.getElementById('qa-next-btn');
  setQaNextError(null);

  if (!q.optional && q.type !== 'appearance_grid'
      && (A[q.key] == null || String(A[q.key]).trim() === '')) {
    setQaNextError(
      q.type === 'choices' ? 'Please pick an option to continue.'
                           : 'Please share your answer to continue.'
    );
    return;
  }

  if (q.type === 'appearance_grid') {
    if (q.relationshipField?.required) {
      const v = A[q.relationshipField.key];
      if (v == null || String(v).trim() === '') {
        setQaNextError('Tell us who you want to make proud.');
        return;
      }
    }
  }

  if (btn) btn.disabled = true;

  try {
    await saveProgress(false);
  } catch (e) {
    console.warn('Progress save failed:', e);
    setQaNextError("We couldn't save your answer. Please check your connection and try again.");
    if (btn) btn.disabled = false;
    return;
  }

  if (btn) btn.disabled = false;

  const card = document.getElementById('qa-card');
  card.classList.add('exit');
  setTimeout(() => {
    card.classList.remove('exit');
    if (cameFromReview) {
      cameFromReview = false;
      renderReview();
      return;
    }
    idx++;
    if (idx >= TOTAL_QS) {
      renderReview();
    } else {
      renderQ();
    }
  }, 250);
}

function qaBack() {
  const card = document.getElementById('qa-card');
  if (inReview) {
    inReview = false;
    idx = TOTAL_QS - 1;
    card.classList.add('exit');
    setTimeout(() => { card.classList.remove('exit'); renderQ(); }, 250);
    return;
  }
  if (cameFromReview) cameFromReview = false;
  if (idx === 0) return;
  card.classList.add('exit');
  setTimeout(() => { idx--; card.classList.remove('exit'); renderQ(); }, 250);
}

function cleanChoiceValue(s) {
  if (typeof s !== 'string') return s;
  const noEmoji = s.replace(/^[^\p{L}\p{N}]+/u, '').trim();
  const noSuffix = noEmoji.replace(/\s*—\s*.*$/, '').trim();
  return noSuffix.toLowerCase();
}

console.assert(cleanChoiceValue('💗 Pink') === 'pink', 'cleanChoiceValue: pink');
console.assert(cleanChoiceValue('💛 Kind — you always look after people') === 'kind', 'cleanChoiceValue: kind');
console.assert(cleanChoiceValue('🏫 A school') === 'a school', 'cleanChoiceValue: a school');
console.assert(cleanChoiceValue('✈️ Flying') === 'flying', 'cleanChoiceValue: flying');
console.assert(cleanChoiceValue('🏖 Beach House') === 'beach house', 'cleanChoiceValue: beach house');

function buildAnswersPayload() {
  const out = {};
  Qs.forEach(q => {
    if (q.type === 'appearance_grid') {
      if (q.relationshipField) {
        const rk = q.relationshipField.key;
        const rv = A[rk];
        if (rv != null && String(rv).trim() !== '') out[rk] = String(rv).trim();
      }
      q.subfields.forEach(sf => {
        if (A[sf.key] != null && A[sf.key] !== '') out[sf.key] = A[sf.key];
      });
      if (q.notesField && A[q.notesField.key] != null && A[q.notesField.key] !== '') {
        out[q.notesField.key] = A[q.notesField.key];
      }
      return;
    }
    if (A[q.key] == null || A[q.key] === '') return;
    if (q.type === 'choices') {
      const v = A[q.key];
      const isObjForm = q.choices.some(c => c !== null && typeof c === 'object');
      const isFreeText = q.allowOther
        && !q.choices.some(c => (typeof c === 'object' ? c.value : c) === v);
      if (isFreeText)      out[q.key] = String(v).trim();
      else if (isObjForm)  out[q.key] = v;
      else                 out[q.key] = cleanChoiceValue(v);
    } else {
      out[q.key] = A[q.key];
    }
  });

  // Storyforge requires both make_proud_person and make_proud_description.
  // We collect one — duplicate it.
  if (out.make_proud_person && !out.make_proud_description) {
    out.make_proud_description = out.make_proud_person;
  }

  return out;
}

async function saveProgress(submitted) {
  return api('/api/answers', {
    method: 'POST',
    body: JSON.stringify({
      answers: buildAnswersPayload(),
      stepCompleted: idx,
      submitted: Boolean(submitted),
    }),
  });
}

function setQaNextError(msg) {
  let el = document.getElementById('qa-next-error');
  if (!el && msg) {
    el = document.createElement('p');
    el.id = 'qa-next-error';
    el.style.cssText = 'color:#C45E38;font-size:.78rem;margin-top:.6rem;text-align:center';
    const nav = document.querySelector('.qa-nav');
    if (nav?.parentNode) nav.parentNode.insertBefore(el, nav.nextSibling);
  }
  if (!el) return;
  if (msg) { el.textContent = msg; el.style.display = 'block'; }
  else     { el.style.display = 'none'; }
}

function setDoneStatus(state, msg) {
  const el = document.getElementById('qa-submit-status');
  if (!el) return;
  const icons = { sending: '⏳', sent: '✅', error: '⚠️' };
  el.textContent   = (icons[state] || '') + ' ' + msg;
  el.className     = 'qa-submit-status qa-submit-status--' + state;
  el.style.display = 'block';
}

async function finishQuestionnaire() {
  document.getElementById('qa-prog-fill').style.width   = '100%';
  document.getElementById('qa-prog-pct').textContent    = '100%';
  document.getElementById('qa-prog-label').textContent  = '🎉 All done!';

  document.getElementById('qa-card').style.display = 'none';

  const doneEl = document.getElementById('qa-done');
  doneEl.style.display = 'block';

  const sumEl = document.getElementById('qa-done-summary');
  if (sumEl) {
    const nameAge = [A.name, A.age].filter(Boolean).join(', age ');
    const highlights = [
      { label: 'Name & age', val: nameAge },
      { label: 'Dream job',  val: A.dream_job },
      { label: 'Superpower', val: A.superpower },
      { label: 'Virtue',     val: A.virtue },
      { label: 'Photo',      val: photos.child_photo.confirmed ? '📸 Uploaded' : (photos.child_photo.skipped ? '📝 Skipped' : '(not uploaded)') },
    ].filter(r => r.val);
    sumEl.innerHTML = highlights.map(r =>
      `<div class="qa-done-summary-row"><span class="qa-done-summary-key">${r.label}</span><span class="qa-done-summary-val">${r.val}</span></div>`,
    ).join('');
  }

  setTimeout(() => doneEl.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);

  setDoneStatus('sending', 'Sending your answers…');
  try {
    await saveProgress(true);
    setDoneStatus('sent', "That's everything we need. We'll be in touch as soon as your book is on its way.");
    api('/api/generate/story', { method: 'POST' }).catch(err => {
      console.warn('Story generation trigger failed (will retry server-side):', err);
    });
  } catch (e) {
    console.error('Final submission failed:', e);
    setDoneStatus('error', "Something went wrong sending your answers. Please email us your order number and we'll sort it straight away.");
  }
}

// Expose for inline onclick handlers in the Liquid
window.openModal = openModal;
window.closeModal = closeModal;
window.validateToken = validateToken;
window.qaNext = qaNext;
window.qaBack = qaBack;
window.wigoUseUpload = (role = 'child_photo') => {
  if (photos[role]) { photos[role].skipped = false; renderQ(); }
};